import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertaImpagos } from "@/components/AlertaImpagos";
import { BajasRecientes } from "@/components/BajasRecientes";
import { CobrosManuales } from "@/components/CobrosManuales";
import { QrRegistro } from "@/components/QrRegistro";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Users, UserCheck, ShieldCheck, MapPin, CalendarRange, GraduationCap, Euro, Percent } from "lucide-react";
import { NIVELES, ESTADO_PAGO } from "@/lib/api";

const Metric = ({ icon: Icon, label, value, testid, accent }) => (
  <Card
    className="rounded-2xl p-6 border-border shadow-sm hover:shadow-md transition-shadow duration-300"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
    </div>
    <p className="font-display text-4xl font-extrabold mt-4">{value}</p>
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [meses, setMeses] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState("todas");

  useEffect(() => {
    api.get("/sedes").then(({ data }) => setSedes(data));
  }, []);

  useEffect(() => {
    const params = sedeId === "todas" ? {} : { sede_id: sedeId };
    setData(null);
    api.get("/dashboard/resumen", { params }).then(({ data }) => setData(data));
    api.get("/dashboard/altas-mensuales", { params }).then(({ data }) => setMeses(data));
  }, [sedeId]);

  const sedeNombre = sedes.find((s) => s.id === sedeId)?.nombre;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Panel general"
        subtitle={sedeNombre ? `Actividad de la sede de ${sedeNombre}`
          : "Resumen de la actividad de Bspark Art Academy"} />

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="dashboard-filtro-sede">
        <button onClick={() => setSedeId("todas")} data-testid="filtro-sede-todas"
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
            sedeId === "todas" ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:bg-muted"}`}>
          Total (todas las sedes)
        </button>
        {sedes.map((s) => (
          <button key={s.id} onClick={() => setSedeId(s.id)} data-testid={`filtro-sede-${s.id}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
              sedeId === s.id ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-muted"}`}>
            {s.nombre}
          </button>
        ))}
      </div>

      <AlertaImpagos />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <Metric icon={Users} label="Alumnos totales" value={data?.total_alumnos ?? "—"} testid="metric-total-alumnos" />
        <Metric icon={UserCheck} label="Alumnos activos" value={data?.alumnos_activos ?? "—"} testid="metric-activos" accent />
        <Metric icon={ShieldCheck} label="Con seguro de recuperación" value={data?.con_seguro ?? "—"} testid="metric-seguro" />
        <Metric icon={Euro} label="Ingreso mensual est." value={data ? `${data.ingresos_mensuales_estimados} €` : "—"} testid="metric-ingresos" />
        <Metric icon={CalendarRange} label="Clases" value={data?.total_clases ?? "—"} testid="metric-clases" />
        <Metric icon={GraduationCap} label="Profesores" value={data?.total_profesores ?? "—"} testid="metric-profesores" />
        <Metric icon={MapPin} label="Sedes" value={data?.total_sedes ?? "—"} testid="metric-sedes" />
        <Metric icon={Percent} label="Comisión Bspark" value={data ? `${data.comision_bspark_acumulada} €` : "—"} testid="metric-comision" accent />
        <Card className="rounded-2xl p-6 border-border shadow-sm sm:col-span-2" data-testid="card-caja">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
            Caja: cobros registrados
          </p>
          <div className="space-y-2 text-sm">
            {[["suscripciones", "Suscripciones", "?grupo=suscripciones&estado=pagado"],
              ["unicos", "Pagos únicos y bonos", "?grupo=unicos&estado=pagado"],
              ["pendientes", "Pendientes de cobro", "?estado=pendiente"],
              ["devoluciones", "Devoluciones", "?estado=devuelto"]].map(([k, label, query]) => (
              <Link key={k} data-testid={`caja-${k}`}
                to={`/admin/pagos${query}${sedeId !== "todas" ? `&sede_id=${sedeId}` : ""}`}
                className="flex justify-between gap-4 -mx-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors duration-200">
                <span className="text-muted-foreground">
                  {label} <span className="text-xs">({data?.caja?.[k]?.num ?? 0})</span>
                </span>
                <span className={`font-bold ${k === "devoluciones" && (data?.caja?.[k]?.importe ?? 0) > 0 ? "text-destructive" : ""}`}>
                  {(data?.caja?.[k]?.importe ?? 0).toFixed(2)} €
                </span>
              </Link>
            ))}
          </div>
          <a href="/admin/pagos" className="inline-flex mt-4 text-sm font-semibold text-primary hover:underline"
            data-testid="ir-a-pagos">Ver todos los pagos y facturas →</a>
          <p className="text-xs text-muted-foreground mt-2">
            Pulsa cualquier línea para ver los alumnos incluidos.
          </p>
        </Card>
        <Card className="rounded-2xl p-6 border-border shadow-sm sm:col-span-2" data-testid="card-comision-mes">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
            Comisión acumulada por mes y sede
          </p>
          {(data?.comision_por_mes || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Todavía no hay cobros registrados. En cuanto se cobre una cuota verás aquí la comisión
              del mes por cada sede, según el porcentaje configurado en su ficha.
            </p>
          ) : (
            <div className="space-y-2 text-sm">
              {data.comision_por_mes.map((c) => (
                <div key={`${c.mes}-${c.sede}`} className="flex justify-between gap-4"
                  data-testid={`comision-${c.mes}-${c.sede}`}>
                  <span className="text-muted-foreground">
                    {c.mes} · {c.sede} <span className="text-xs">({c.comision_pct}%)</span>
                  </span>
                  <span className="font-bold">{c.comision.toFixed(2)} €</span>
                </div>
              ))}
            </div>
          )}
          {(data?.comision_por_sede || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-border space-y-1.5 text-sm">
              {data.comision_por_sede.map((c) => (
                <div key={c.sede} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Total {c.sede}</span>
                  <span className="font-semibold">{c.comision.toFixed(2)} €</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-suscripciones">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
            Suscripciones activas
          </p>
          <div className="space-y-2 text-sm">
            {["mensual", "trimestral", "semestral", "anual", "bonos", "sin_cuota"].map((k) => (
              <div key={k} className="flex justify-between" data-testid={`suscripciones-${k}`}>
                <span className="capitalize text-muted-foreground">
                  {k === "bonos" ? "Bonos de clases" : k === "sin_cuota" ? "Sin cuota activa" : k}
                </span>
                <span className="font-bold">{data?.suscripciones?.[k] ?? 0}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-niveles">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
            Alumnos por nivel
          </p>
          <div className="space-y-2 text-sm">
            {NIVELES.map((n) => (
              <div key={n.key} className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className={`h-2 w-2 rounded-full ${n.dot}`} /> {n.label}
                </span>
                <span className="font-bold">{data?.niveles?.[n.key] ?? 0}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-pagos">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
            Estado de pagos
          </p>
          <div className="space-y-2 text-sm">
            {Object.entries(ESTADO_PAGO).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-muted-foreground">{v.label}</span>
                <span className="font-bold">{data?.pagos?.[k] ?? 0}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <BajasRecientes />
      <CobrosManuales />

      <div className="mb-8">
        <QrRegistro />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <Card className="rounded-2xl p-6 xl:col-span-2 border-border shadow-sm" data-testid="chart-altas">
          <h3 className="font-display text-lg font-bold mb-6">Altas de alumnos por mes</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={meses}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="mes" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 12,
                    color: "hsl(var(--popover-foreground))",
                  }}
                />
                <Bar dataKey="alumnos" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="chart-sedes">
          <h3 className="font-display text-lg font-bold mb-6">Alumnos activos por sede</h3>
          {(!data?.por_sede || data.por_sede.length === 0) && (
            <p className="text-sm text-muted-foreground">Aún no hay sedes registradas.</p>
          )}
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.por_sede || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis type="category" dataKey="sede" width={90} stroke="hsl(var(--muted-foreground))" fontSize={12}
                  tickFormatter={(v) => (v && v.length > 12 ? `${v.slice(0, 12)}…` : v)} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 12,
                  }}
                />
                <Bar dataKey="alumnos" radius={[0, 6, 6, 0]}>
                  {(data?.por_sede || []).map((_, i) => (
                    <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
